export interface Language {
  iso: string
  label: string
}

/** Languages the dashboard itself is translated into. */
export const UI_LANGUAGES: Language[] = [
  { iso: 'en', label: 'English' },
  { iso: 'fr', label: 'French' },
  { iso: 'it', label: 'Italian' },
]

/**
 * Canonical ISO 639-1 vocabulary for authored content.
 *
 * Validation uses this list, never `Intl.DisplayNames`: runtime support is a
 * presentation capability and must not decide whether persisted data is valid.
 */
export const CONTENT_LANGUAGE_CODES = [
  'aa',
  'ab',
  'ae',
  'af',
  'ak',
  'am',
  'an',
  'ar',
  'as',
  'av',
  'ay',
  'az',
  'ba',
  'be',
  'bg',
  'bh',
  'bi',
  'bm',
  'bn',
  'bo',
  'br',
  'bs',
  'ca',
  'ce',
  'ch',
  'co',
  'cr',
  'cs',
  'cu',
  'cv',
  'cy',
  'da',
  'de',
  'dv',
  'dz',
  'ee',
  'el',
  'en',
  'eo',
  'es',
  'et',
  'eu',
  'fa',
  'ff',
  'fi',
  'fj',
  'fo',
  'fr',
  'fy',
  'ga',
  'gd',
  'gl',
  'gn',
  'gu',
  'gv',
  'ha',
  'he',
  'hi',
  'ho',
  'hr',
  'ht',
  'hu',
  'hy',
  'hz',
  'ia',
  'id',
  'ie',
  'ig',
  'ii',
  'ik',
  'io',
  'is',
  'it',
  'iu',
  'ja',
  'jv',
  'ka',
  'kg',
  'ki',
  'kj',
  'kk',
  'kl',
  'km',
  'kn',
  'ko',
  'kr',
  'ks',
  'ku',
  'kv',
  'kw',
  'ky',
  'la',
  'lb',
  'lg',
  'li',
  'ln',
  'lo',
  'lt',
  'lu',
  'lv',
  'mg',
  'mh',
  'mi',
  'mk',
  'ml',
  'mn',
  'mr',
  'ms',
  'mt',
  'my',
  'na',
  'nb',
  'nd',
  'ne',
  'ng',
  'nl',
  'nn',
  'no',
  'nr',
  'nv',
  'ny',
  'oc',
  'oj',
  'om',
  'or',
  'os',
  'pa',
  'pi',
  'pl',
  'ps',
  'pt',
  'qu',
  'rm',
  'rn',
  'ro',
  'ru',
  'rw',
  'sa',
  'sc',
  'sd',
  'se',
  'sg',
  'si',
  'sk',
  'sl',
  'sm',
  'sn',
  'so',
  'sq',
  'sr',
  'ss',
  'st',
  'su',
  'sv',
  'sw',
  'ta',
  'te',
  'tg',
  'th',
  'ti',
  'tk',
  'tl',
  'tn',
  'to',
  'tr',
  'ts',
  'tt',
  'tw',
  'ty',
  'ug',
  'uk',
  'ur',
  'uz',
  've',
  'vi',
  'vo',
  'wa',
  'wo',
  'xh',
  'yi',
  'yo',
  'za',
  'zh',
  'zu',
] as const

export type ContentLanguageCode = (typeof CONTENT_LANGUAGE_CODES)[number]

const contentLanguageCodes = new Set<string>(CONTENT_LANGUAGE_CODES)

export const isContentLanguageCode = (value: string): value is ContentLanguageCode =>
  contentLanguageCodes.has(value)

type DisplayNamesInstance = { of: (code: string) => string | undefined }
type DisplayNamesConstructor = new (
  locales: string | string[],
  options: { type: 'language'; fallback?: 'code' | 'none' }
) => DisplayNamesInstance

const displayName = (code: string, locale: string): string => {
  const { DisplayNames } = Intl as typeof Intl & { DisplayNames?: DisplayNamesConstructor }
  if (!DisplayNames) return code

  try {
    const name = new DisplayNames(locale, { type: 'language', fallback: 'code' }).of(code) ?? code
    return `${name.charAt(0).toLocaleUpperCase(locale)}${name.slice(1)}`
  } catch {
    return code
  }
}

export const getContentLanguageEndonym = (code: string): string => displayName(code, code)

export const getContentLanguageDisplayName = (code: string, uiLocale: string): string =>
  displayName(code, uiLocale)

export const normalizeLanguageSearch = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase()
    .trim()

/** Compatibility shape for existing selectors; `unset` is not a content language. */
export const CONTENT_LANGUAGES: Language[] = [
  { iso: 'unset', label: 'Not selected' },
  ...CONTENT_LANGUAGE_CODES.map((iso) => ({ iso, label: getContentLanguageEndonym(iso) })),
]
